/**
 * Utility functions for constructing Google Places photo URLs pointing to backend endpoint.
 * 
 * Backend endpoint provides browser caching headers (30-day TTL) and MongoDB caching
 * while keeping the API key server-side for security.
 * 
 * Supports both:
 * - Old format: photo_reference (e.g., 'CmRaAAAA...' or 'places/ChIJ.../photos/ABC...')
 * - New format: name (e.g., 'places/ChIJ.../photos/AZLasH...')
 */

import { debug, warn } from './logger';

/**
 * Gets the backend API base URL from environment variables
 * 
 * CRITICAL: Sanitize the environment variable value because Vercel environment variables
 * can sometimes contain trailing whitespace or newlines when set via the dashboard.
 * Next.js embeds NEXT_PUBLIC_* variables at build time, so any whitespace gets baked
 * into the JavaScript bundle and can break URL construction.
 */
function getBackendApiUrl(): string {
  const rawEnvValue = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
  const defaultUrl = 'https://api.globethrivers.com';
  
  // Sanitize the environment variable value - remove leading/trailing whitespace and newlines
  // This is critical because Vercel env vars can have trailing whitespace/newlines when set via dashboard
  // We only trim and remove newlines/tabs, not internal spaces (though URLs shouldn't have spaces anyway)
  const sanitizedEnvValue = rawEnvValue 
    ? rawEnvValue.trim().replace(/[\n\r\t]/g, '')
    : null;
  
  const url = sanitizedEnvValue || defaultUrl;
  
  // Log the backend API URL being used (only once per page load to avoid spam)
  if (typeof window !== 'undefined' && !(window as Window & { __loggedBackendApiUrl?: boolean }).__loggedBackendApiUrl) {
    const wasSanitized = rawEnvValue && rawEnvValue !== sanitizedEnvValue;
    
    debug('[googlePhotoProxy] Backend API URL', {
      url,
      fromEnv: !!rawEnvValue,
      wasSanitized,
    });
    
    if (wasSanitized && sanitizedEnvValue) {
      warn('[googlePhotoProxy] Environment variable NEXT_PUBLIC_APP_API_BASE_URL was sanitized (removed whitespace/newlines)', {
        originalValue: rawEnvValue,
        sanitizedValue: sanitizedEnvValue,
        originalLength: rawEnvValue.length,
        sanitizedLength: sanitizedEnvValue.length,
      });
    }
    
    (window as Window & { __loggedBackendApiUrl?: boolean }).__loggedBackendApiUrl = true;
  }
  
  return url;
}

/**
 * Constructs a URL for Google Places photos pointing to the backend endpoint.
 * Supports both old format (photo_reference) and new format (name).
 * 
 * @param photoReference - The photo_reference from Google Places API (old format)
 * @param size - Size for the photo in pixels (default: 400, applied to both width and height)
 * @returns Backend endpoint URL that will fetch and cache the Google Places photo
 * 
 * @example
 * const url = getGooglePhotoProxyUrl('CmRaAAAA...', 400);
 * // Returns: 'https://api.globethrivers.com/api/v1/public/places/photo?photoRef=CmRaAAAA...&size=400'
 */
export function getGooglePhotoProxyUrl(
  photoReference: string,
  size: number = 400
): string {
  if (!photoReference) {
    return '';
  }

  // Determine parameter name based on format
  // New format starts with 'places/' -> use 'name'
  // Old format short reference (e.g., 'CmRaAAAA...') -> use 'photo_reference'
  // Current format that starts with 'places/' but passed as photo_reference -> use 'photoRef'
  let paramName: string;
  if (photoReference.startsWith('places/')) {
    // If it's the new format (places/...), use 'name' parameter
    paramName = 'name';
  } else {
    // Old format short reference (e.g., 'CmRaAAAA...'), use 'photo_reference' parameter
    paramName = 'photo_reference';
  }
  
  const params = new URLSearchParams({
    [paramName]: photoReference,
    size: String(size),
  });

  const url = `${getBackendApiUrl()}/api/v1/public/places/photo?${params.toString()}`;
  
  // Sanitize URL - ensure no whitespace or newlines
  // CSS backgroundImage is very sensitive to whitespace in URLs
  return url.trim().replace(/\s+/g, '').replace(/[\n\r\t]/g, '');
}

/**
 * Constructs a URL for Google Places photos using the new format (name field).
 * 
 * @param photoName - The name from Google Places API (new format, e.g., 'places/ChIJ.../photos/AZLasH...')
 * @param size - Size for the photo in pixels (default: 512, applied to both width and height)
 * @returns Backend endpoint URL that will fetch and cache the Google Places photo
 * 
 * @example
 * const url = getGooglePhotoProxyUrlFromName('places/ChIJ.../photos/AZLasH...', 512);
 * // Returns: 'https://api.globethrivers.com/api/v1/public/places/photo?name=places/ChIJ.../photos/AZLasH...&size=512'
 */
export function getGooglePhotoProxyUrlFromName(
  photoName: string,
  size: number = 512
): string {
  if (!photoName) {
    return '';
  }

  const params = new URLSearchParams({
    name: photoName,
    size: String(size),
  });

  const url = `${getBackendApiUrl()}/api/v1/public/places/photo?${params.toString()}`;
  
  // Sanitize URL - ensure no whitespace or newlines
  // CSS backgroundImage is very sensitive to whitespace in URLs
  return url.trim().replace(/\s+/g, '').replace(/[\n\r\t]/g, '');
}

/**
 * Unified function that handles both photo formats (name or photo_reference).
 * 
 * @param photo - Photo object with either name or photo_reference field
 * @param size - Size for the photo in pixels (default: 512)
 * @returns Backend endpoint URL
 */
export function getGooglePhotoUrl(
  photo: { name?: string; photo_reference?: string },
  size: number = 512
): string {
  let url = '';
  if (photo.name) {
    url = getGooglePhotoProxyUrlFromName(photo.name, size);
  } else if (photo.photo_reference) {
    url = getGooglePhotoProxyUrl(photo.photo_reference, size);
  }
  
  return url;
}


